import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import SettingsContext from "../contexts/SettingsContext";
import UserContext from "../contexts/UserContext";
import Data from "./Data/Data";
import Duels from "./Duels/Duels";
import Home from "./Index/Home";
import UserData from "./User Data/UserData";
import IronSights from "./IronSights/IronSights";
import Leaderboards from "./Leaderboards/Leaderboards";
import Login from "./Login/Login";
import NotFound from "./NotFound";
import Settings from "./Settings/Settings";
import { User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/firebase";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  Firestore,
  setDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import "../App.css";

/**
 * Handles routing and contexts
 * @returns the appropriate react component
 */
const App = () => {
  // for settings context
  const [backgroundColor, setBackgroundColor] = useState("#000000"); // background color in game
  const [projectileColor, setProjectileColor] = useState("#ef4444"); // projectile color in game
  const [targetColors, setTargetColors] = useState([
    "#06b6d4",
    "#0ea5e9",
    "#3b82f6",
    "#6366f1",
    "#8b5cf6",
    "#a855f7",
  ]); // target colors in game
  const [targetVel, setTargetVel] = useState(0.23); // target's velocity in iron sights
  const [targetAcc, setTargetAcc] = useState(-0.0045); // target's acceleration in iron sights
  const [duration, setDuration] = useState(2000); // target's duration in duels

  // for user context
  const [user, setUser] = useState<User | null>(null); // stored user, null if doesn't exist
  const [loggedIn, setLoggedIn] = useState(false); // boolean stating whether the user is logged in or not
  const [db, setDb] = useState<Firestore | null>(null); // db for queries
  const [loadedUserSettings, setLoadedUserSettings] = useState(false); // to determine to load user settings

  // config for firebase
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID,
  };

  useEffect(() => {
    const app = initializeApp(firebaseConfig); // Initialize Firebase

    setDb(getFirestore(app)); // Initialize Cloud Firestore and get a reference to the service
  }, []);

  // handle loggedIn
  useEffect(() => {
    setLoggedIn(user !== null);
  }, [user]);

  // handle auth state change
  useEffect(() => {
    onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
  }, []);

  /**
   * A function to load a logged in user's custom settings.
   * @param user The logged in user.
   * @param db The connected firestore database.
   */
  async function setUserSettings(user: User, db: Firestore) {
    let userSettingsRef = doc(db, "settings", user.uid);
    let settingPrefs = await getDoc(userSettingsRef);

    if (settingPrefs.exists()) {
      let data = settingPrefs.data();

      if (data.backgroundColor) {
        setBackgroundColor(data.backgroundColor);
      }
      if (data.projectileColor) {
        setProjectileColor(data.projectileColor);
      }
      if (data.targetColors) {
        setTargetColors(data.targetColors);
      }
      if (data.targetVel) {
        setTargetVel(data.targetVel);
      }
      if (data.targetAcc) {
        setTargetAcc(data.targetAcc);
      }
      if (data.duration) {
        setDuration(data.duration);
      }
    }
  }

  //load user's custom settings
  useEffect(() => {
    if (user && db && !loadedUserSettings) {
      setUserSettings(user, db);
      setLoadedUserSettings(true);
    } else if (!user || !db) {
      setLoadedUserSettings(false);
    }
  }, [user, db]);

  /**
   * Set background color in db and react state
   * @param color is the new background color
   */
  const logAndSetBackgroundColor = (color: string) => {
    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          backgroundColor: color,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging background preference");
      });
    }

    setBackgroundColor(color);
  };

  /**
   * Set projectile color in db and react state
   * @param color is the new projectile color
   */
  const logAndSetProjectileColor = (color: string) => {
    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          projectileColor: color,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging projectile preference");
      });
    }

    setProjectileColor(color);
  };

  /**
   * Sets target colors in db and react state
   * @param updateFunc updates the current target colors appropiately
   */
  const logAndSetTargetColors = (updateFunc: (cur: string[]) => string[]) => {
    const newTargetColors = updateFunc(targetColors);

    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          targetColors: newTargetColors,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging targets preference");
      });
    }

    setTargetColors(newTargetColors);
  };

  /**
   * Set target vel in db and react state
   * @param vel is the new target vel
   */
  const logAndSetTargetVel = (vel: number) => {
    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          targetVel: vel,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging targetVel preference");
      });
    }

    setTargetVel(vel);
  };

  /**
   * Set target acc in db and react state
   * @param acc is the new target acc
   */
  const logAndSetTargetAcc = (acc: number) => {
    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          targetAcc: acc,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging targetAcc preference");
      });
    }

    setTargetAcc(acc);
  };

  /**
   * Set duration in db and react state
   * @param dur is the new duration
   */
  const logAndSetDuration = (dur: number) => {
    if (db && user) {
      setDoc(
        doc(db, "settings", user.uid),
        {
          duration: dur,
        },
        { merge: true }
      ).catch((e) => {
        console.log("Error logging duration preference");
      });
    }

    setDuration(dur);
  };

  // Uses contexts to pass around data (rather than using props everywhere which will get messy)
  return (
    <SettingsContext.Provider
      value={{
        backgroundColor,
        logAndSetBackgroundColor,
        projectileColor,
        logAndSetProjectileColor,
        targetColors,
        logAndSetTargetColors,
        targetVel,
        logAndSetTargetVel,
        targetAcc,
        logAndSetTargetAcc,
        duration,
        logAndSetDuration,
      }}
    >
      <UserContext.Provider value={{ user, setUser, loggedIn, db }}>
        <BrowserRouter>
          <Routes>
            <Route index element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/iron-sights" element={<IronSights />} />
            <Route path="/duels" element={<Duels />} />
            <Route path="/data" element={<Data />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/leaderboards" element={<Leaderboards />} />
            <Route path="/user-data" element={<UserData />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </UserContext.Provider>
    </SettingsContext.Provider>
  );
};

export default App;
